import React, { useState, useEffect, useRef } from 'react';
import {
  IconButton,
  Grid,
  Divider,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@material-ui/core';
import { QRCodeSVG } from 'qrcode.react';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import fileDownload from 'js-file-download';

import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import { alpha } from '@material-ui/core/styles/colorManipulator';

import Notification from '../../utils/notification';
import DeleteDialog from '../../components/DeleteDialog';
import { useLayoutDispatch, toggleLoading } from '../../context/LayoutContext';
import api from '../../services/api';
import { getError } from '../../utils/getResponseError';
import { dateWithHours, parseISO, basicDate } from '../../utils/dateFormatter';
import { ESTADO_FATURA } from '../../constants/estado-fatura';

const getStatusColor = (estadoId) => {
  switch (estadoId) {
    case ESTADO_FATURA.PAGO:
      return 'success';
    case ESTADO_FATURA.PENDENTE:
      return 'warning';
    case ESTADO_FATURA.PROCESSANDO:
      return 'primary';
    case ESTADO_FATURA.VENCIDO:
      return 'error';
    default:
      return 'default';
  }
};

export default function FaturaDetalhesDialog({
  open,
  onClose,
  faturaId,
  onStatusChange,
}) {
  const [fatura, setFatura] = useState(null);
  const [openBaixa, setOpenBaixa] = useState(null);
  const [loadingBaixa, setLoadingBaixa] = useState(false);
  const [motivo, setMotivo] = useState('ACERTOS');
  const [proximoEstado, setProximoEstado] = useState(6);
  const pollingRef = useRef(null);
  const layoutDispatch = useLayoutDispatch();

  useEffect(() => {
    if (!open || !faturaId) {
      setFatura(null);
      return;
    }
    fetchFatura();
  }, [open, faturaId]);

  useEffect(() => () => stopPolling(), []);

  function stopPolling() {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }

  async function fetchFatura() {
    toggleLoading(layoutDispatch);
    try {
      const { data } = await api.get(`/fatura/${faturaId}`);
      setFatura(data);
      if (
        data.id_estado_fatura === ESTADO_FATURA.PENDENTE
        || data.id_estado_fatura === ESTADO_FATURA.PROCESSANDO
        || data.id_estado_fatura === ESTADO_FATURA.VENCIDO
      ) {
        startPolling(faturaId);
      }
    } catch (error) {
      Notification({
        type: 'report',
        message: getError(error),
        variant: 'contained',
        color: 'secondary',
      });
      onClose();
    }
    toggleLoading(layoutDispatch);
  }

  function startPolling(id) {
    stopPolling();
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/fatura/${id}`);
        setFatura(data);
        if (
          data.id_estado_fatura !== ESTADO_FATURA.PENDENTE
          && data.id_estado_fatura !== ESTADO_FATURA.PROCESSANDO
          && data.id_estado_fatura !== ESTADO_FATURA.VENCIDO
        ) {
          stopPolling();
        }
      } catch (error) {
        console.error('Error polling fatura:', error);
        stopPolling();
      }
    }, 5000);
    pollingRef.current = interval;
  }

  function handleClose() {
    stopPolling();
    setFatura(null);
    onClose();
  }

  async function copyToClipboard(text, label) {
    try {
      await navigator.clipboard.writeText(text);
      Notification({
        type: 'shipped',
        message: `${label} copiado para a área de transferência`,
        variant: 'contained',
        color: 'success',
      });
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        Notification({
          type: 'shipped',
          message: `${label} copiado para a área de transferência`,
          variant: 'contained',
          color: 'success',
        });
      } catch (fallbackError) {
        Notification({
          type: 'report',
          message: 'Erro ao copiar para a área de transferência',
          variant: 'contained',
          color: 'secondary',
        });
      }
      document.body.removeChild(textArea);
    }
  }

  async function handleBaixa() {
    const day = new Date().getDay();
    if (day === 0 || day === 6) {
      Notification({
        type: 'report',
        message: 'Apenas é possível dar baixa em dias úteis',
        variant: 'contained',
        color: 'secondary',
      });
      return;
    }
    setLoadingBaixa(true);
    try {
      await api.post(`/fatura/baixa/${openBaixa}`, {
        motivo,
        proximoEstado,
      });
      Notification({
        type: 'shipped',
        message: 'Sucesso',
        variant: 'contained',
        color: 'success',
      });
      fetchFatura();
      if (typeof onStatusChange === 'function' && fatura) {
        const { data } = await api.get(`/fatura/${openBaixa}`);
        onStatusChange(data);
      }
    } catch (error) {
      Notification({
        type: 'report',
        message: getError(error),
        variant: 'contained',
        color: 'secondary',
      });
    }
    setOpenBaixa(null);
    setLoadingBaixa(false);
  }

  async function handlePDF() {
    if (!fatura) return;
    try {
      const { data } = await api.get(`/fatura/${fatura.nosso_numero}/download`, {
        responseType: 'blob',
      });
      fileDownload(data, `fatura_${fatura.id_fatura}.pdf`);
    } catch (error) {
      Notification({
        type: 'report',
        message: 'Erro ao gerar o boleto',
        variant: 'contained',
        color: 'secondary',
      });
    }
  }

  return (
    <>
      <DeleteDialog
        open={!!openBaixa}
        handleClose={() => setOpenBaixa(null)}
        handleConfirm={handleBaixa}
        message={openBaixa ? `Tem certeza que deseja dar baixa na fatura #${openBaixa}?` : ''}
        deleteText={loadingBaixa ? <CircularProgress size={20} /> : 'Dar baixa'}
        title="Baixa manual"
      >
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="motivo-baixa-label">Motivo da baixa (para o banco)</InputLabel>
              <Select
                labelId="motivo-baixa-label"
                value={motivo}
                name="motivo"
                onChange={(e) => setMotivo(e.target.value)}
                required
              >
                <MenuItem value="ACERTOS">Baixa para acertos</MenuItem>
                <MenuItem value="PAGODIRETOAOCLIENTE">Baixado, pago direto ao cliente</MenuItem>
                <MenuItem value="APEDIDODOCLIENTE">Baixado a pedido do cliente</MenuItem>
                <MenuItem value="SUBSTITUICAO">Baixado para substituição</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="estado-fatura-label">Estado da fatura</InputLabel>
              <Select
                labelId="estado-fatura-label"
                value={proximoEstado}
                name="proximoEstado"
                onChange={(e) => setProximoEstado(Number(e.target.value))}
                required
              >
                <MenuItem value={2}>Pago</MenuItem>
                <MenuItem value={4}>Cancelado</MenuItem>
                <MenuItem value={6}>Baixa</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DeleteDialog>

      <Dialog
        onClose={handleClose}
        aria-labelledby="fatura-dialog-title"
        open={open}
        BackdropProps={{
          className: '',
          style: { backgroundColor: alpha('#246E47', 0.3) },
        }}
        fullWidth
        maxWidth="lg"
      >
        <MuiDialogTitle id="fatura-dialog-title">
          Detalhes da fatura
          {fatura && ` #${fatura.id_fatura}`}
        </MuiDialogTitle>
        <MuiDialogContent dividers>
          {fatura && (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} mb={3}>
                    {(fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE
                      || fatura.id_estado_fatura === ESTADO_FATURA.VENCIDO) && (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          type="button"
                          onClick={() => setOpenBaixa(fatura.id_fatura)}
                        >
                          Baixa
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          type="button"
                          onClick={handlePDF}
                        >
                          PDF
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom align="center">
                    Detalhes da fatura
                  </Typography>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    p={2}
                    border={1}
                    borderColor="grey.200"
                    borderRadius={8}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" color="textSecondary">
                        ID Banco Inter:
                      </Typography>
                      <Typography variant="body1" style={{ fontWeight: 500 }}>
                        {fatura.banco_inter}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" color="textSecondary">
                        Estado atual:
                      </Typography>
                      <Chip
                        size="small"
                        label={fatura.EstadosFatura && fatura.EstadosFatura.nome}
                        color={getStatusColor(fatura.id_estado_fatura)}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" color="textSecondary">
                        Vencimento:
                      </Typography>
                      <Typography variant="body1" style={{ fontWeight: 500 }}>
                        {basicDate(parseISO(fatura.vencimento))}
                      </Typography>
                    </Box>
                    <Divider />
                    {(fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE
                      || fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO
                      || fatura.id_estado_fatura === ESTADO_FATURA.VENCIDO) && (
                      <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle1" color="textSecondary">
                            Código de barras:
                          </Typography>
                          <Tooltip title="Copiar código de barras">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(fatura.codigo_de_barras, 'Código de barras')}
                            >
                              <FileCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography
                          variant="body2"
                          style={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
                        >
                          {fatura.codigo_de_barras}
                        </Typography>
                      </Box>
                    )}
                    <Box mt={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" color="textSecondary">
                          Link da fatura:
                        </Typography>
                        <Tooltip title="Copiar link da fatura">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(
                              `${process.env.REACT_APP_BASE_URL || ''}/#/fatura/${fatura.nosso_numero}`,
                              'Link da fatura'
                            )}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography
                        variant="body2"
                        style={{
                          wordBreak: 'break-all',
                          fontFamily: 'monospace',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          color: '#1976d2',
                        }}
                        onClick={() => window.open(
                          `${process.env.REACT_APP_BASE_URL || ''}/#/fatura/${fatura.nosso_numero}`,
                          '_blank'
                        )}
                      >
                        {`${process.env.REACT_APP_BASE_URL || ''}/#/fatura/${fatura.nosso_numero}`}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  {(() => {
                    const isAguardando = fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE
                      || fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO
                      || fatura.id_estado_fatura === ESTADO_FATURA.VENCIDO;
                    if (isAguardando && fatura.pix_copia_e_cola) {
                      return (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          p={2}
                          border={1}
                          borderColor="grey.300"
                          borderRadius={8}
                        >
                          <Typography variant="h6" gutterBottom align="center">
                            PIX QR Code
                          </Typography>
                          <Box mb={2}>
                            <QRCodeSVG
                              value={fatura.pix_copia_e_cola}
                              size={200}
                              level="M"
                              includeMargin
                            />
                          </Box>
                          <Typography variant="body2" color="textSecondary" align="center" mb={2}>
                            Escaneie o QR Code para pagar via PIX
                          </Typography>
                          <Box width="100%" mb={2}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Typography variant="subtitle2" color="textSecondary">
                                PIX Copia e Cola:
                              </Typography>
                              <Tooltip title="Copiar código PIX">
                                <IconButton
                                  size="small"
                                  onClick={() => copyToClipboard(fatura.pix_copia_e_cola, 'Código PIX')}
                                >
                                  <FileCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Typography
                              variant="body2"
                              style={{
                                wordBreak: 'break-all',
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                              }}
                            >
                              {fatura.pix_copia_e_cola}
                            </Typography>
                          </Box>
                          {(fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE
                            || fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO) && (
                            <Box mt={1}>
                              <Chip
                                size="small"
                                label={
                                  fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO
                                    ? 'Processando pagamento...'
                                    : 'Aguardando pagamento...'
                                }
                                color={
                                  fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO
                                    ? 'primary'
                                    : 'warning'
                                }
                                icon={<CircularProgress size={12} />}
                                style={{ padding: '8px 12px' }}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    }
                    if (fatura.id_estado_fatura === ESTADO_FATURA.PAGO) {
                      return (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          p={4}
                          border={1}
                          borderColor="success.light"
                          borderRadius={8}
                          minHeight={300}
                          style={{ backgroundColor: '#e8f5e8' }}
                        >
                          <Typography
                            variant="h6"
                            style={{ color: '#2e7d32' }}
                            align="center"
                            gutterBottom
                          >
                            ✅ Pagamento Confirmado
                          </Typography>
                          <Typography variant="body1" style={{ color: '#2e7d32' }} align="center">
                            Esta fatura foi paga com sucesso
                          </Typography>
                        </Box>
                      );
                    }
                    return (
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        p={4}
                        border={1}
                        borderColor="grey.200"
                        borderRadius={8}
                        minHeight={300}
                      >
                        <Typography variant="body1" color="textSecondary" align="center">
                          QR Code PIX não disponível
                        </Typography>
                      </Box>
                    );
                  })()}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom align="center">
                    Histórico da fatura
                  </Typography>
                  {fatura.HistoricoFaturas && fatura.HistoricoFaturas.length > 0 ? (
                    <TableContainer component={Paper} style={{ overflowX: 'auto', minWidth: 300 }}>
                      <Table size="small" aria-label="historico table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Estado</TableCell>
                            <TableCell align="right">Data</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fatura.HistoricoFaturas.map((row) => (
                            <TableRow key={row.id_historico_fatura}>
                              <TableCell component="th" scope="row">
                                {row.EstadosFatura && row.EstadosFatura.nome}
                              </TableCell>
                              <TableCell align="right">
                                {dateWithHours(parseISO(row.criado_em))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      p={4}
                      border={1}
                      borderColor="grey.200"
                      borderRadius={8}
                    >
                      <Typography variant="body2" color="textSecondary">
                        Nenhum histórico disponível
                      </Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom align="center">
                    Notificações enviadas
                  </Typography>
                  {fatura.Envios && fatura.Envios.length > 0 ? (
                    <TableContainer component={Paper} style={{ overflowX: 'auto', minWidth: 400 }}>
                      <Table size="small" aria-label="notificacoes table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Meio</TableCell>
                            <TableCell align="right">Estado</TableCell>
                            <TableCell align="right">Data</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fatura.Envios.map((row) => (
                            <TableRow key={row.id_historico_fatura}>
                              <TableCell component="th" scope="row">
                                {row.ServicosDeEnvio && row.ServicosDeEnvio.nome}
                              </TableCell>
                              <TableCell align="right">{row.EstadosEnvio && row.EstadosEnvio.nome}</TableCell>
                              <TableCell align="right">
                                {row.atualizado_em
                                  ? dateWithHours(parseISO(row.atualizado_em))
                                  : dateWithHours(parseISO(row.criado_em))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      p={4}
                      border={1}
                      borderColor="grey.200"
                      borderRadius={8}
                    >
                      <Typography variant="body2" color="textSecondary">
                        Nenhuma notificação enviada
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </MuiDialogContent>
        <MuiDialogActions>
          <Button autoFocus onClick={handleClose} color="primary">
            Fechar
          </Button>
        </MuiDialogActions>
      </Dialog>
    </>
  );
}
